import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  searchTranslationMemory,
  getTranslationMemoryVersions,
} from '../api';
import { getColumnLabelsForTm, getTmDisplayName } from '../utils/languages';

const PAGE_SIZE = 20;
// Backend caps any single TM request to 5000 rows. We use a small
// default for browse / search (fast: 5-30 DDB scan pages) and only
// raise to the hard cap when the user explicitly clicks Export. This
// keeps the initial tab open snappy against ~70k active TM rows.
const TM_BROWSE_LIMIT = 500;
const TM_EXPORT_LIMIT = 5000;
// Light client-side debounce on the search input so each keystroke
// doesn't fire a scan against the TM.
const SEARCH_DEBOUNCE_MS = 350;

function formatDate(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (err) {
    return iso;
  }
}

function targetText(it) {
  return it?.spanish || it?.target || '';
}

const ALL_TM_ID = 'ALL';

function TranslationMemory({ selectedTmId, availableTMs = [], onTmChange }) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [exactMatch, setExactMatch] = useState(false);
  const [items, setItems] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [expandedKey, setExpandedKey] = useState(null);
  const [versionCache, setVersionCache] = useState({});
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Local TM override: Translation Memory tab defaults to "ALL" and
  // manages its own selection independently from the global TM selector.
  const [localTmId, setLocalTmId] = useState(ALL_TM_ID);
  const effectiveTmId = localTmId;
  const isAllTm = effectiveTmId === ALL_TM_ID;

  const tmOptionsWithAll = useMemo(() => {
    const allOption = { tm_id: ALL_TM_ID, label: 'All Translation Memories' };
    return [allOption, ...availableTMs];
  }, [availableTMs]);

  const selectedTm = useMemo(
    () => availableTMs.find((t) => t.tm_id === effectiveTmId) || {},
    [availableTMs, effectiveTmId],
  );
  const columnLabels = useMemo(() => getColumnLabelsForTm(selectedTm), [selectedTm]);
  const tmLabel = isAllTm ? 'all translation memories' : getTmDisplayName(effectiveTmId, availableTMs);

  useEffect(() => {
    setVersionCache({});
    setExpandedKey(null);
  }, [effectiveTmId]);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  const runSearch = useCallback(async (q, { limit = TM_BROWSE_LIMIT, exact = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await searchTranslationMemory(q, limit, { exact, tm_id: effectiveTmId });
      setItems(resp.items || []);
      setTruncated(Boolean(resp.truncated));
      setPage(0);
      setExpandedKey(null);
      return resp;
    } catch (err) {
      setError('Failed to search Translation Memory: ' + err.message);
      setItems([]);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [effectiveTmId]);

  // Initial load + react to debounced query changes / match-mode flip.
  useEffect(() => {
    runSearch(debouncedQuery, { exact: exactMatch });
  }, [debouncedQuery, exactMatch, runSearch]);

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const pagedItems = useMemo(() => {
    const start = page * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  }, [items, page]);

  const rangeStart = items.length === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min((page + 1) * PAGE_SIZE, items.length);

  const handleToggleExpand = async (english) => {
    if (expandedKey === english) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(english);
    if (versionCache[english]) return;
    setVersionsLoading(true);
    try {
      const versions = await getTranslationMemoryVersions(english, effectiveTmId);
      setVersionCache((prev) => ({ ...prev, [english]: versions }));
    } catch (err) {
      console.warn('Failed to load TM versions:', err);
    } finally {
      setVersionsLoading(false);
    }
  };

  // Export the current filtered set. The browse view is capped at
  // TM_BROWSE_LIMIT for fast paint, so when the user clicks Export we
  // re-fetch with the higher TM_EXPORT_LIMIT cap to give them a fuller
  // slice. We never let users dump the entire ~70k-row TM in one shot -
  // they should narrow the search first if a row they want isn't here.
  const handleExport = async () => {
    setExporting(true);
    try {
      let exportRows = items;
      if (items.length >= TM_BROWSE_LIMIT) {
        try {
          const resp = await searchTranslationMemory(
            debouncedQuery,
            TM_EXPORT_LIMIT,
            { exact: exactMatch, tm_id: effectiveTmId }
          );
          exportRows = resp.items || items;
          setItems(exportRows);
          setTruncated(Boolean(resp.truncated));
        } catch (err) {
          console.warn('Falling back to currently loaded rows for export:', err);
        }
      }
      if (exportRows.length === 0) return;
      const sourceHeader = isAllTm ? 'English' : columnLabels.source;
      const targetHeader = isAllTm ? 'Target' : columnLabels.target;
      const headers = isAllTm
        ? ['#', 'TM', sourceHeader, targetHeader, 'Version', 'Source', 'Category', 'Notes', 'Updated At', 'Created At']
        : ['#', sourceHeader, targetHeader, 'Version', 'Source', 'Category', 'Notes', 'Updated At', 'Created At'];
      const sheetRows = exportRows.map((it, idx) => {
        const row = {
          '#': idx + 1,
          [sourceHeader]: it.english,
          [targetHeader]: targetText(it),
          'Version': it.version,
          'Source': it.source,
          'Category': it.category,
          'Notes': it.notes,
          'Updated At': it.updated_at,
          'Created At': it.created_at,
        };
        if (isAllTm) row['TM'] = it.tm_display_name || it.tm_id || '';
        return row;
      });
      const worksheet = XLSX.utils.json_to_sheet(sheetRows, { header: headers });
      const cols = isAllTm
        ? [{ wch: 5 }, { wch: 22 }, { wch: 60 }, { wch: 60 }, { wch: 8 }, { wch: 16 }, { wch: 18 }, { wch: 40 }, { wch: 20 }, { wch: 20 }]
        : [{ wch: 5 }, { wch: 60 }, { wch: 60 }, { wch: 8 }, { wch: 16 }, { wch: 18 }, { wch: 40 }, { wch: 20 }, { wch: 20 }];
      worksheet['!cols'] = cols;
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Translation Memory');
      const stamp = new Date().toISOString().slice(0, 10);
      const tag = debouncedQuery ? debouncedQuery.replace(/[^a-z0-9]+/gi, '-').slice(0, 30) : 'all';
      XLSX.writeFile(workbook, `translation-memory-${tag}-${stamp}.xlsx`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-header-content">
          <div className="step-number">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
          </div>
          <div className="card-header-text">
            <h2>Translation Memory</h2>
            <p>
              Search <strong>{tmLabel || 'translation memory'}</strong> ({columnLabels.pairLabel}) and review previous linguist versions
            </p>
          </div>
          <div className="card-header-helper">
            Search by {columnLabels.source} or {columnLabels.target} text. Click any row to expand and see
            its full version history with linguist comments.
          </div>
        </div>
      </div>
      <div className="card-body">
        <div className="tm-selector-block">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="tm-tab-translationMemory">
                Translation Memory
              </label>
              <select
                id="tm-tab-translationMemory"
                className="form-select"
                value={localTmId}
                onChange={(e) => setLocalTmId(e.target.value)}
                disabled={loading}
              >
                {tmOptionsWithAll.map((tm) => (
                  <option key={tm.tm_id} value={tm.tm_id}>{tm.label}</option>
                ))}
              </select>
            </div>
            {!isAllTm && (
              <>
                <div className="form-group">
                  <label className="form-label">Source Language</label>
                  <select className="form-select" value={columnLabels.sourceCode} disabled>
                    <option>{columnLabels.source} ({columnLabels.sourceCode})</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Target Language</label>
                  <select className="form-select" value={columnLabels.targetCode} disabled>
                    <option>{columnLabels.target} ({columnLabels.targetCode})</option>
                  </select>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="tm-toolbar">
          <input
            type="search"
            className="tm-search"
            placeholder={
              exactMatch
                ? 'Exact phrase (e.g. "Account")'
                : `Search by ${columnLabels.source} or ${columnLabels.target} (substring)...`
            }
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search Translation Memory"
          />
          <label
            className="tm-toggle"
            title={
              exactMatch
                ? `Currently: exact match. Returns only rows where the entire ${columnLabels.source} or ${columnLabels.target} text equals your query.`
                : 'Currently: substring match. Returns rows that contain your query anywhere in the text.'
            }
          >
            <input
              type="checkbox"
              checked={exactMatch}
              onChange={(e) => setExactMatch(e.target.checked)}
              aria-label="Exact match toggle"
            />
            <span>Exact match</span>
          </label>
          <button
            type="button"
            className="btn btn-secondary btn-small"
            onClick={() => runSearch(debouncedQuery, { exact: exactMatch })}
            disabled={loading}
            title="Refresh results"
          >
            {loading ? <span className="spinner"></span> : '↻ Refresh'}
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-small"
            onClick={handleExport}
            disabled={items.length === 0 || exporting}
            title="Download the current filtered results as an Excel file"
          >
            {exporting ? (
              <>
                <span className="spinner"></span>
                Exporting...
              </>
            ) : (
              `↓ Export ${items.length} row${items.length === 1 ? '' : 's'} (XLSX)`
            )}
          </button>
        </div>

        <div className="tm-status-bar">
          {loading ? (
            'Loading...'
          ) : (
            <>
              {items.length === 0
                ? `No ${exactMatch ? 'exact ' : ''}matches${debouncedQuery ? ` for "${debouncedQuery}"` : ''}`
                : `Showing ${rangeStart}-${rangeEnd} of ${items.length} ${exactMatch ? 'exact ' : ''}match${items.length === 1 ? '' : 'es'}${
                    debouncedQuery ? ` for "${debouncedQuery}"` : ''
                  }`}
              {truncated && (
                <span className="tm-truncated-warning">
                  · Showing the first {items.length.toLocaleString()} rows. Refine your search to find more
                  specific matches.
                </span>
              )}
            </>
          )}
        </div>

        {error && <div className="error-banner">{error}</div>}

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '32px' }}></th>
                {isAllTm && <th>TM</th>}
                <th>{isAllTm ? 'English' : columnLabels.source}</th>
                <th>{isAllTm ? 'Target' : columnLabels.target}</th>
                <th>Version</th>
                <th>Updated</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {pagedItems.length === 0 && !loading && (
                <tr>
                  <td colSpan={isAllTm ? 7 : 6} className="empty-state-row">
                    No translations match the current filter.
                  </td>
                </tr>
              )}
              {pagedItems.map((it) => {
                const isExpanded = expandedKey === it.english;
                const versions = versionCache[it.english] || [];
                return (
                  <React.Fragment key={`${it.tm_id || ''}__${it.english}__${it.version}`}>
                    <tr
                      className={isExpanded ? 'tm-row-expanded' : ''}
                      onClick={() => handleToggleExpand(it.english)}
                    >
                      <td className="tm-expand-cell">
                        <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                      </td>
                      {isAllTm && (
                        <td>
                          <span className="type-badge">{it.tm_display_name || it.tm_id || '-'}</span>
                        </td>
                      )}
                      <td className="text-wrap">{it.english}</td>
                      <td className="text-wrap">{targetText(it)}</td>
                      <td>v{it.version}</td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                        {formatDate(it.updated_at || it.created_at)}
                      </td>
                      <td>
                        <span className="type-badge">{it.source || '-'}</span>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="tm-versions-row">
                        <td colSpan={isAllTm ? 7 : 6}>
                          {versionsLoading && versions.length === 0 ? (
                            <div className="loading-requests">
                              <span className="spinner"></span> Loading version history...
                            </div>
                          ) : versions.length === 0 ? (
                            <div className="tm-versions-empty">No history available.</div>
                          ) : (
                            <table className="data-table tm-versions-table">
                              <thead>
                                <tr>
                                  {isAllTm && <th>TM</th>}
                                  <th>Version</th>
                                  <th>{isAllTm ? 'Target' : columnLabels.target}</th>
                                  <th>Source</th>
                                  <th>Notes</th>
                                  <th>Updated</th>
                                </tr>
                              </thead>
                              <tbody>
                                {versions.map((v) => (
                                  <tr key={`${v.tm_id || ''}__${v.version}`} className={v.active ? 'tm-version-active' : ''}>
                                    {isAllTm && (
                                      <td>
                                        <span className="type-badge">{v.tm_display_name || v.tm_id || '-'}</span>
                                      </td>
                                    )}
                                    <td>
                                      v{v.version}
                                      {v.active && <span className="tm-version-active-badge">active</span>}
                                    </td>
                                    <td className="text-wrap">{targetText(v)}</td>
                                    <td>{v.source || '-'}</td>
                                    <td className="text-wrap">{v.notes || '-'}</td>
                                    <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                                      {formatDate(v.updated_at || v.created_at)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination-bar">
            <button
              className="btn btn-ghost btn-small"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              ← Previous
            </button>
            <span className="pagination-info">
              Page {page + 1} of {totalPages}
            </span>
            <button
              className="btn btn-ghost btn-small"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default TranslationMemory;
